import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SystemStats, UserManagementStats, ApprovalStats, ProjectRevenueStats } from '@/types';
import apiService from '@/services/api';
import Card from '@/components/ui/Card';
import { Users, FolderKanban, Activity, Clock, CheckCircle, AlertCircle, TrendingUp, DollarSign } from 'lucide-react';

interface AdminSectionsProps {
  maxHeight?: number;
}

const AdminSections: React.FC<AdminSectionsProps> = ({ maxHeight }) => {
  const navigate = useNavigate();
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);
  const [userStats, setUserStats] = useState<UserManagementStats | null>(null);
  const [approvalStats, setApprovalStats] = useState<ApprovalStats | null>(null);
  const [revenueStats, setRevenueStats] = useState<ProjectRevenueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const containerStyle = maxHeight ? { height: `${maxHeight}px` } : {};

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      const [system, users, approvals, revenue] = await Promise.all([
        apiService.getSystemStats(),
        apiService.getUserManagementStats(),
        apiService.getApprovalStats(),
        apiService.getRevenueStats().catch(() => null) // 收入统计可能失败，不影响其他数据
      ]);
      
      setSystemStats(system);
      setUserStats(users);
      setApprovalStats(approvals);
      setRevenueStats(revenue);
    } catch (error: any) {
      setError('获取管理员统计数据失败');
      console.error('Failed to fetch admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 overflow-y-auto" style={containerStyle}>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 overflow-y-auto" style={containerStyle}>
        <Card>
          <Card.Content className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchAdminStats}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              重试
            </button>
          </Card.Content>
        </Card>
      </div>
    );
  }

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, onClick }) => (
    <div 
      className={`p-4 rounded-lg border-2 transition-all ${
        onClick ? 'cursor-pointer hover:shadow-md hover:border-opacity-80' : ''
      } ${color}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className="opacity-80">
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 overflow-y-auto" style={containerStyle}>
      
      {/* 系统整体统计 */}
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-purple-600" />
            <span>系统统计</span>
          </Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard
              title="总用户数"
              value={systemStats?.total_users || 0}
              icon={<Users className="h-8 w-8 text-blue-600" />}
              color="border-blue-200 bg-blue-50"
              onClick={() => navigate('/users')}
            />
            <StatCard
              title="总项目数"
              value={systemStats?.total_projects || 0}
              icon={<FolderKanban className="h-8 w-8 text-green-600" />}
              color="border-green-200 bg-green-50"
              onClick={() => navigate('/projects')}
            />
            <StatCard
              title="总工时"
              value={`${(systemStats?.total_work_hours || 0).toFixed(1)}h`}
              icon={<Clock className="h-8 w-8 text-orange-600" />}
              color="border-orange-200 bg-orange-50"
              onClick={() => navigate('/reports')}
            />
            <StatCard
              title="活跃用户"
              value={systemStats?.active_users || 0}
              icon={<Users className="h-8 w-8 text-purple-600" />}
              color="border-purple-200 bg-purple-50"
            />
            <StatCard
              title="已完成项目"
              value={systemStats?.completed_projects || 0}
              icon={<CheckCircle className="h-8 w-8 text-green-600" />}
              color="border-green-200 bg-green-50"
            />
            <StatCard
              title="总成本"
              value={`¥${(systemStats?.total_cost || 0).toLocaleString()}`}
              icon={<Activity className="h-8 w-8 text-red-600" />}
              color="border-red-200 bg-red-50"
            />
          </div>
        </Card.Content>
      </Card>

      {/* 收入与财务统计 */}
      {revenueStats && (
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <span>收入与财务</span>
            </Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                title="已确认收入"
                value={`¥${(revenueStats.confirmed_revenue || 0).toLocaleString()}`}
                icon={<DollarSign className="h-8 w-8 text-green-600" />}
                color="border-green-200 bg-green-50"
                onClick={() => navigate('/project-revenues')}
              />
              <StatCard
                title="待确认收入"
                value={`¥${(revenueStats.pending_revenue || 0).toLocaleString()}`}
                icon={<Clock className="h-8 w-8 text-yellow-600" />}
                color="border-yellow-200 bg-yellow-50"
                onClick={() => navigate('/project-revenues')}
              />
              <StatCard
                title="总利润"
                value={`¥${((revenueStats.confirmed_revenue || 0) - (systemStats?.total_cost || 0)).toLocaleString()}`}
                icon={<TrendingUp className="h-8 w-8 text-blue-600" />}
                color="border-blue-200 bg-blue-50"
                onClick={() => navigate('/reports')}
              />
              <StatCard
                title="收入记录数"
                value={revenueStats.revenue_count}
                icon={<Activity className="h-8 w-8 text-purple-600" />}
                color="border-purple-200 bg-purple-50"
                onClick={() => navigate('/project-revenues')}
              />
              <StatCard
                title="待确认记录"
                value={revenueStats.pending_count}
                icon={<AlertCircle className="h-8 w-8 text-orange-600" />}
                color="border-orange-200 bg-orange-50"
                onClick={() => navigate('/project-revenues')}
              />
              <StatCard
                title="利润率"
                value={(revenueStats.confirmed_revenue || 0) > 0 ? 
                  `${(((revenueStats.confirmed_revenue || 0) - (systemStats?.total_cost || 0)) / (revenueStats.confirmed_revenue || 1) * 100).toFixed(1)}%` : 
                  '0%'
                }
                icon={<CheckCircle className="h-8 w-8 text-indigo-600" />}
                color="border-indigo-200 bg-indigo-50"
              />
            </div>
          </Card.Content>
        </Card>
      )}

      {/* 用户管理和审批统计 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 用户管理统计 */}
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span>用户管理</span>
            </Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200 cursor-pointer hover:bg-yellow-100 transition-colors"
                onClick={() => navigate('/users')}
              >
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">待审批用户</span>
                </div>
                <span className="text-xl font-bold text-yellow-600">
                  {userStats?.pending_approvals || 0}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">新注册用户</span>
                </div>
                <span className="text-xl font-bold text-blue-600">
                  {userStats?.new_registrations || 0}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3">
                  <Activity className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">今日活跃</span>
                </div>
                <span className="text-xl font-bold text-green-600">
                  {userStats?.today_active || 0}
                </span>
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* 审批统计 */}
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-orange-600" />
              <span>待审批事项</span>
            </Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors"
                onClick={() => navigate('/work-records')}
              >
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <span className="font-medium text-orange-800">工作记录</span>
                </div>
                <span className="text-xl font-bold text-orange-600">
                  {approvalStats?.pending_work_records || 0}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                onClick={() => navigate('/work-records')}
              >
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-purple-600" />
                  <span className="font-medium text-purple-800">请假申请</span>
                </div>
                <span className="text-xl font-bold text-purple-600">
                  {approvalStats?.pending_leave_requests || 0}
                </span>
              </div>
              
              <div 
                className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200 cursor-pointer hover:bg-indigo-100 transition-colors"
                onClick={() => navigate('/work-records')}
              >
                <div className="flex items-center space-x-3">
                  <FolderKanban className="h-5 w-5 text-indigo-600" />
                  <span className="font-medium text-indigo-800">出差申请</span>
                </div>
                <span className="text-xl font-bold text-indigo-600">
                  {approvalStats?.pending_business_trips || 0}
                </span>
              </div>
            </div>
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};

export default AdminSections;